import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../config';
import { useApp } from '../context/AppContext';
import '../App.css';

interface PaymentTransaction {
  id: string;
  stripe_checkout_session_id: string;
  stripe_payment_intent_id: string;
  amount: number;
  currency: string;
  status: 'pending' | 'succeeded' | 'failed' | 'refunded';
  description: string;
  receipt_url?: string;
  receipt_email: string;
  is_refunded: boolean;
  stripe_refund_id?: string;
  refund_amount?: number;
  refund_reason?: string;
  refunded_at?: string;
  created_at: string;
}

const PaymentHistory: React.FC = () => {
  const { state, dispatch } = useApp();
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refundingId, setRefundingId] = useState<string | null>(null);

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  const fetchPaymentHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please login to view payment history');
        setLoading(false);
        return;
      }

      const response = await fetch(API_ENDPOINTS.PAYMENT_HISTORY, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Payment history response status:', response.status);
      console.log('Payment history response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Payment history error response:', errorText);
        throw new Error(`Failed to fetch payment history: ${response.status} - ${errorText}`);
      }

      // Check if response has content
      const contentType = response.headers.get('content-type');
      console.log('Response content-type:', contentType);

      if (!contentType || !contentType.includes('application/json')) {
        const responseText = await response.text();
        console.error('Invalid content type:', contentType);
        console.error('Response body:', responseText);
        throw new Error(`Server returned invalid response format. Content-Type: ${contentType}, Body: ${responseText.substring(0, 100)}`);
      }

      const responseText = await response.text();
      console.log('Response text:', responseText);

      if (!responseText || responseText.trim() === '') {
        throw new Error('Server returned empty response');
      }

      const data = JSON.parse(responseText);
      setTransactions(data.transactions || []);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      setLoading(false);
    }
  };

  const handleRefund = async (transactionId: string) => {
    if (!window.confirm('Are you sure you want to request a refund? This will revoke your access to full therapist profiles.')) {
      return;
    }

    const reason = window.prompt('Please provide a reason for the refund (optional):');

    try {
      setRefundingId(transactionId);
      const token = localStorage.getItem('token');

      const response = await fetch(API_ENDPOINTS.PAYMENT_REFUND, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transaction_id: transactionId,
          reason: reason || '',
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to process refund');
      }

      const data = await response.json();
      alert(data.message || 'Refund processed successfully');

      // Update user's has_paid status in context
      if (state.user) {
        dispatch({
          type: 'SET_USER',
          payload: {
            ...state.user,
            has_paid: false, // Revoke access after refund
          },
        });
        console.log('[PaymentHistory] Updated user has_paid to false after refund');
      }

      // Refresh transaction list
      await fetchPaymentHistory();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to process refund');
    } finally {
      setRefundingId(null);
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    const dollars = amount / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(dollars);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string, isRefunded: boolean) => {
    if (isRefunded) {
      return <span className="payment-status-badge status-refunded">Refunded</span>;
    }

    switch (status) {
      case 'succeeded':
        return <span className="payment-status-badge status-succeeded">Succeeded</span>;
      case 'pending':
        return <span className="payment-status-badge status-pending">Pending</span>;
      case 'failed':
        return <span className="payment-status-badge status-failed">Failed</span>;
      default:
        return <span className="payment-status-badge">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="payment-history-container">
        <div className="payment-history-loading">Loading payment history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-history-container">
        <div className="payment-history-error">Error: {error}</div>
      </div>
    );
  }

  const handleGoBack = () => {
    // Navigate back to main screen using history API (no page reload)
    const conversationId = sessionStorage.getItem('conversationId');
    console.log("[PaymentHistory] Back button clicked, conversationId:", conversationId);

    // Use history API for client-side navigation
    window.history.pushState({}, "", "/");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  return (
    <div className="payment-history-container">
      {/* Mobile Header Bar */}
      <div className="payment-history-mobile-header">
        <button
          onClick={handleGoBack}
          className="payment-history-back-icon"
          aria-label="Go back"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M19 12H5M5 12L12 19M5 12L12 5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <span className="payment-history-mobile-title">Payment History</span>
      </div>

      {/* Content Area */}
      <div className="payment-history-header">
        <h1>Payment History</h1>
        <p className="payment-history-subtitle">View all your payment transactions</p>
      </div>

      {transactions.length === 0 ? (
        <div className="payment-history-empty">
          <p>No payment history found</p>
        </div>
      ) : (
        <div className="payment-history-list">
          {transactions.map((transaction) => (
            <div key={transaction.id} className="payment-history-item">
              <div className="payment-item-header">
                <div className="payment-item-title">
                  <h3>{transaction.description}</h3>
                  {getStatusBadge(transaction.status, transaction.is_refunded)}
                </div>
                <div className="payment-item-amount">
                  {formatAmount(transaction.amount, transaction.currency)}
                </div>
              </div>

              <div className="payment-item-details">
                <div className="payment-detail-row">
                  <span className="payment-detail-label">Date:</span>
                  <span className="payment-detail-value">{formatDate(transaction.created_at)}</span>
                </div>
                <div className="payment-detail-row">
                  <span className="payment-detail-label">Transaction ID:</span>
                  <span className="payment-detail-value payment-id">{transaction.id}</span>
                </div>
                {transaction.receipt_email && (
                  <div className="payment-detail-row">
                    <span className="payment-detail-label">Receipt Email:</span>
                    <span className="payment-detail-value">{transaction.receipt_email}</span>
                  </div>
                )}
                {transaction.is_refunded && transaction.refunded_at && (
                  <>
                    <div className="payment-detail-row">
                      <span className="payment-detail-label">Refunded:</span>
                      <span className="payment-detail-value">{formatDate(transaction.refunded_at)}</span>
                    </div>
                    {transaction.refund_amount && (
                      <div className="payment-detail-row">
                        <span className="payment-detail-label">Refund Amount:</span>
                        <span className="payment-detail-value">
                          {formatAmount(transaction.refund_amount, transaction.currency)}
                        </span>
                      </div>
                    )}
                    {transaction.refund_reason && (
                      <div className="payment-detail-row">
                        <span className="payment-detail-label">Refund Reason:</span>
                        <span className="payment-detail-value">{transaction.refund_reason}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {transaction.status === 'succeeded' && !transaction.is_refunded && (
                <div className="payment-item-actions">
                  <button
                    className="payment-refund-button"
                    onClick={() => handleRefund(transaction.id)}
                    disabled={refundingId === transaction.id}
                  >
                    {refundingId === transaction.id ? 'Processing...' : 'Request Refund'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;
